import {
    ReactNode,
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from 'react'
import { createPortal } from 'react-dom'
import { MdCheck, MdExpandMore } from 'react-icons/md'

export interface DropdownOption {
    value: number | ''
    label: string
    /** Cor da bolinha: nome semântico do tema ou hexadecimal. Sem cor, some. */
    color?: string | null
    /** Linha secundária (andar, descrição da sala). */
    hint?: string | null
}

interface OptionDropdownProps {
    options: DropdownOption[]
    value: number | ''
    onChange: (value: number | '') => void
    placeholder?: string
    disabled?: boolean
    ariaLabel: string
    className?: string
    /**
     * Só a seta, sem o rótulo do item selecionado. É a metade direita de um
     * botão dividido: o texto de ação fica no botão vizinho.
     */
    compact?: boolean
    /**
     * Texto fixo no gatilho, no lugar do item selecionado. Serve ao botão que
     * EXECUTA uma ação ao escolher ("Definir"), e não guarda um valor.
     */
    triggerLabel?: string
    /** Ícone à esquerda do rótulo. */
    icon?: ReactNode
    /**
     * `select` (padrão) parece um campo de formulário: fundo claro e borda
     * neutra. `action` não impõe cor nenhuma — quem estiliza é o `className` de
     * quem chama, como no botão "Definir", que é primário. Sem essa separação,
     * o `bg-base-100` da aparência de campo vencia o `btn-primary` e o botão
     * saía branco, com o texto claro sumindo dentro dele.
     */
    variant?: 'select' | 'action'
}

/**
 * Classes da bolinha por cor do tema.
 *
 * Escritas por extenso, e não com `bg-${color}`: o Tailwind varre o código-fonte
 * em busca de classes literais, e a interpolada simplesmente não existe no CSS
 * gerado — a bolinha sairia transparente.
 */
const DOT_CLASSES: Record<string, string> = {
    error: 'bg-error',
    warning: 'bg-warning',
    accent: 'bg-accent',
    success: 'bg-success',
    info: 'bg-info',
    primary: 'bg-primary',
    secondary: 'bg-secondary',
    neutral: 'bg-neutral',
}

/** Bolinha da cor: classe do tema quando semântica, estilo inline quando hex. */
function ColorDot({ color }: { color?: string | null }) {
    if (!color) return null
    const custom = /^#[0-9a-fA-F]{6}$/.test(color)
    return (
        <span
            className={`h-3 w-3 shrink-0 rounded-full border border-base-300 ${
                custom ? '' : (DOT_CLASSES[color] ?? 'bg-neutral')
            }`}
            style={custom ? { backgroundColor: color } : undefined}
        />
    )
}

/**
 * Seletor com cor à vista.
 *
 * Existe porque `<select>` nativo não pinta as opções: o controle de presença é
 * lido pela COR no balcão, e escolher "Emergência" numa lista cinza obriga a ler
 * texto justamente na hora em que ninguém tem tempo de ler.
 *
 * O menu é renderizado em portal no `body`, com posição fixa calculada pelo
 * botão. Um `dropdown-content` comum é recortado pelo `overflow-y-auto` do
 * modal — e recorte por overflow não se resolve com z-index, que é o remendo
 * intuitivo e errado. Enquanto aberto, ele acompanha scroll e resize.
 */
export default function OptionDropdown({
    options,
    value,
    onChange,
    placeholder = 'Selecione',
    disabled = false,
    ariaLabel,
    className = '',
    compact = false,
    triggerLabel,
    icon,
    variant = 'select',
}: OptionDropdownProps) {
    const [open, setOpen] = useState(false)
    const [position, setPosition] = useState({ top: 0, left: 0, width: 0 })
    /**
     * Onde o menu é montado.
     *
     * `<dialog>` aberto com `showModal()` vive no TOP LAYER do navegador: um
     * portal no `body` fica atrás dele e não recebe clique nenhum, por mais
     * z-index que se ponha — o menu aparecia inteiro e não respondia. Dentro de
     * um modal, então, o destino é o próprio `<dialog>`; fora dele, o `body`.
     * Nos dois casos o menu escapa do `overflow-y-auto` que o recortaria.
     */
    const [container, setContainer] = useState<HTMLElement | null>(null)
    const triggerRef = useRef<HTMLButtonElement>(null)
    const menuRef = useRef<HTMLDivElement>(null)

    const selected = options.find(option => option.value === value)

    const reposition = useCallback(() => {
        const trigger = triggerRef.current
        if (!trigger) return
        const rect = trigger.getBoundingClientRect()
        // Num gatilho estreito (a seta do botão dividido) o menu não cabe na
        // largura dele: usa o mínimo e alinha pela DIREITA, para não cobrir a
        // linha inteira nem sair da tela.
        const width = Math.max(rect.width, 220)
        const alignRight = rect.width < width
        // Abre para cima quando não há espaço embaixo — o painel costuma ter
        // linhas coladas no rodapé do modal.
        const spaceBelow = window.innerHeight - rect.bottom
        const openUp = spaceBelow < 240 && rect.top > spaceBelow
        setPosition({
            top: openUp ? Math.max(8, rect.top - 8) : rect.bottom + 4,
            left: Math.min(
                Math.max(8, alignRight ? rect.right - width : rect.left),
                Math.max(8, window.innerWidth - width - 8)
            ),
            width,
        })
    }, [])

    useLayoutEffect(() => {
        if (!open) return
        setContainer(triggerRef.current?.closest('dialog') ?? document.body)
        reposition()
    }, [open, reposition])

    useEffect(() => {
        if (!open) return

        const onScrollOrResize = () => reposition()
        const onPointerDown = (event: MouseEvent) => {
            const target = event.target as Node
            if (
                menuRef.current?.contains(target) ||
                triggerRef.current?.contains(target)
            ) {
                return
            }
            setOpen(false)
        }
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setOpen(false)
        }

        // `true` na captura: pega o scroll do container do modal também, não só
        // o da janela.
        window.addEventListener('scroll', onScrollOrResize, true)
        window.addEventListener('resize', onScrollOrResize)
        document.addEventListener('mousedown', onPointerDown)
        document.addEventListener('keydown', onKeyDown)
        return () => {
            window.removeEventListener('scroll', onScrollOrResize, true)
            window.removeEventListener('resize', onScrollOrResize)
            document.removeEventListener('mousedown', onPointerDown)
            document.removeEventListener('keydown', onKeyDown)
        }
    }, [open, reposition])

    return (
        <>
            <button
                ref={triggerRef}
                type="button"
                className={`btn btn-sm gap-2 font-normal ${
                    variant === 'select' && !compact
                        ? 'justify-between border-base-300 bg-base-100 !rounded-lg hover:bg-base-200'
                        : ''
                } ${className}`}
                disabled={disabled}
                aria-label={ariaLabel}
                aria-haspopup="listbox"
                aria-expanded={open}
                onClick={() => setOpen(current => !current)}
            >
                {!compact && (
                    <span className="flex min-w-0 items-center gap-2">
                        {icon}
                        {!triggerLabel && <ColorDot color={selected?.color} />}
                        <span className="truncate">
                            {triggerLabel ??
                                (selected ? selected.label : placeholder)}
                        </span>
                    </span>
                )}
                {compact && icon}
                {compact && selected?.color && (
                    <ColorDot color={selected.color} />
                )}
                <MdExpandMore
                    className={`shrink-0 text-base ${compact ? '' : 'opacity-60'}`}
                />
            </button>

            {open &&
                container &&
                createPortal(
                    <div
                        ref={menuRef}
                        role="listbox"
                        aria-label={ariaLabel}
                        className="fixed z-[999] max-h-64 overflow-y-auto rounded-xl border border-base-300 bg-base-100 p-1 shadow-xl"
                        style={{
                            top: position.top,
                            left: position.left,
                            width: position.width,
                        }}
                    >
                        {options.map(option => {
                            const active = option.value === value
                            return (
                                <button
                                    key={String(option.value)}
                                    type="button"
                                    role="option"
                                    aria-selected={active}
                                    className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm hover:bg-base-200 ${
                                        active ? 'bg-base-200 font-medium' : ''
                                    }`}
                                    onClick={() => {
                                        onChange(option.value)
                                        setOpen(false)
                                    }}
                                >
                                    <ColorDot color={option.color} />
                                    <span className="min-w-0 flex-1">
                                        <span className="block truncate">
                                            {option.label}
                                        </span>
                                        {option.hint &&
                                            option.hint.trim() !==
                                                option.label.trim() && (
                                                <span className="block truncate text-xs text-base-content/60">
                                                    {option.hint}
                                                </span>
                                            )}
                                    </span>
                                    {active && (
                                        <MdCheck className="shrink-0 text-base text-primary" />
                                    )}
                                </button>
                            )
                        })}
                    </div>,
                    container
                )}
        </>
    )
}
